import { Checkbox } from '@/components/ui/checkbox'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useId } from 'react'
import { PATCH_SALARY_WORK_TIME_LEGACY_DIET_MUTATION } from './mutations'
import type { DietSettingsSalaryWorkTime } from './queries'
import { DIET_SETTINGS_SALARY_WORK_TIMES_QUERY } from './queries'
import { SalaryWorkTimeHeading } from './salary-work-time-heading'

type LegacyOption =
  | 'useAutomaticDietGeneration'
  | 'useAutomaticSalaryDayGeneration'
  | 'useAdvancedCalculation'
  | 'useSixHourRuleForCalculation'

/** Each salary setting's own diet and salary day switches for the legacy
 * calculation, used while no generation rule covers the output. */
export const LegacyDietSection: React.FC = () => {
  const texts = useTranslate({
    title: 'settings.diet-settings.legacy-per-salary-setting',
    description: 'settings.diet-settings.legacy-description',
    noSalarySettings: 'settings.generation-rules.no-salary-settings',
  })
  const { data } = useQuery(DIET_SETTINGS_SALARY_WORK_TIMES_QUERY)
  const salaryWorkTimes = data?.allSalaryWorkTimes ?? []

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="space-y-2 px-5 py-4">
        <h2 className="text-lg font-semibold text-stone-900">{texts.title}</h2>
        <p className="text-sm text-stone-600">{texts.description}</p>
      </div>
      {data && salaryWorkTimes.length === 0 && (
        <p className="border-t border-stone-200 px-5 py-4 text-sm text-stone-600">
          {texts.noSalarySettings}
        </p>
      )}
      {salaryWorkTimes.map(salaryWorkTime => (
        <LegacyDietOptions
          key={salaryWorkTime.id}
          salaryWorkTime={salaryWorkTime}
        />
      ))}
    </section>
  )
}

const LegacyDietOptions: React.FC<{
  salaryWorkTime: DietSettingsSalaryWorkTime
}> = ({ salaryWorkTime }) => {
  const texts = useTranslate({
    useAutomaticDietGeneration: 'settings.use-automatic-diet-generation',
    useAutomaticSalaryDayGeneration:
      'settings.use-automatic-salary-day-generation',
    useAdvancedCalculation: 'settings.use-advanced-diet-calculation',
    useSixHourRuleForCalculation: 'settings.use-six-hour-rule-for-diet',
  })
  const [patch, { loading }] = useMutation(
    PATCH_SALARY_WORK_TIME_LEGACY_DIET_MUTATION
  )

  const generating =
    salaryWorkTime.useAutomaticDietGeneration ||
    salaryWorkTime.useAutomaticSalaryDayGeneration
  const options: LegacyOption[] = [
    'useAutomaticDietGeneration',
    'useAutomaticSalaryDayGeneration',
    ...(generating ? (['useAdvancedCalculation'] as const) : []),
    ...(generating && salaryWorkTime.useAdvancedCalculation
      ? (['useSixHourRuleForCalculation'] as const)
      : []),
  ]

  function toggle(option: LegacyOption, value: boolean) {
    void patch({
      variables: { id: salaryWorkTime.id, input: { [option]: value } },
    })
  }

  return (
    <div className="flex flex-col gap-2 border-t border-stone-200 px-5 py-4">
      <SalaryWorkTimeHeading salaryWorkTime={salaryWorkTime} />
      {options.map(option => (
        <LegacyOptionToggle
          key={option}
          label={texts[option]}
          value={salaryWorkTime[option]}
          disabled={loading}
          onChange={value => toggle(option, value)}
        />
      ))}
    </div>
  )
}

const LegacyOptionToggle: React.FC<{
  label: string
  value: boolean
  disabled: boolean
  onChange(value: boolean): void
}> = ({ label, value, disabled, onChange }) => {
  const id = useId()
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        size="md"
        value={value}
        disabled={disabled}
        onChange={checked => onChange(checked === true)}
      />
      <label htmlFor={id} className="text-sm text-stone-700">
        {label}
      </label>
    </div>
  )
}
